/**
 * Stand-demo settings menu — phones only (Lenny, 14/10/2026).
 *
 * On an iPhone the title, the two control pills and the toolbar do not fit
 * one row, and a second row of pills looked wrong. So on a phone the pills
 * are not built at all: a cog joins the toolbar row, left of the Scenes
 * button, and opens a dropdown with everything that was spread around the
 * screen:
 *
 *   Classic            the control schemes, one marked (walk scenes only)
 *   Hold to walk
 *   ▶  Flythrough      the camera tour's own button, moved in here as a
 *                      36 px square (adoptTour); pause the same way
 *   Q  Quality         a square; tap Q and the Auto / High / Medium / Low
 *                      segments unfold on the row (the settings panel's
 *                      QualityPicker; the tutorial never mentions it)
 *
 * Picking a scheme or starting the flythrough closes the menu; so does the
 * cog again, or a tap anywhere else. The tutorial points a bubble at the
 * cog and, the first time the menu opens, explains the options under it
 * (ui/demo-tutorial.ts: 'settings' and the menu guide). iPad and desktop
 * keep the pills and the ▶ at the bottom; mount.ts decides by the screen's
 * short side (PHONE_MAX_SHORT_SIDE).
 */

import type { SchemeId } from '../experiments/control-schemes';
import type { DemoChoice } from './demo-controls';
import { resolveLang, type UiLang } from './lang';
import { ICON_COG } from './settings-panel';

/** A phone: the screen's short side is under this (CSS px). The iPhone 17
 *  Pro is 402 either way round; the iPad Air is 820 × 1180, the mini 744. */
export const PHONE_MAX_SHORT_SIDE = 600;

export function isPhoneScreen(): boolean {
    return Math.min(window.innerWidth, window.innerHeight) < PHONE_MAX_SHORT_SIDE;
}

type Copy = { settings: string; flythrough: string; quality: string };
const COPY: Record<UiLang, Copy> = {
    en: { settings: 'Settings', flythrough: 'Flythrough', quality: 'Quality' },
    es: { settings: 'Ajustes',  flythrough: 'Recorrido',  quality: 'Calidad' },
    ca: { settings: 'Ajustos',  flythrough: 'Recorregut', quality: 'Qualitat' }
};

export interface DemoMenuOptions {
    /** The toolbar row the cog joins, and the button it goes in front of. */
    toolbar: HTMLElement;
    before: Element | null;
    /** Where the dropdown panel lives (the ui root). */
    parent: HTMLElement;
    /** The schemes, or null on an orbit scene (no scheme rows). */
    choices: DemoChoice[] | null;
    initial: SchemeId;
    onChoose: (id: SchemeId) => void;
    onOpen?: () => void;
    onClose?: () => void;
}

export class DemoMenu {

    /** The cog — the tutorial's bubble points at it. */
    readonly cog: HTMLButtonElement;

    /** The dropdown — the tutorial's guide bubble hangs under it. */
    readonly panel: HTMLDivElement;

    /** Where mount.ts inserts the QualityPicker (shown once Q is tapped). */
    readonly qualitySlot: HTMLElement;

    private tourRow: HTMLElement;

    private buttons = new Map<SchemeId, HTMLButtonElement>();

    private opened = false;

    private onDocDown: (e: PointerEvent) => void;

    constructor(private opts: DemoMenuOptions) {
        const t = COPY[resolveLang()];

        this.cog = document.createElement('button');
        this.cog.className = 'ollo-btn ollo-toolbar-btn ollo-demo-cog';
        this.cog.type = 'button';
        this.cog.innerHTML = ICON_COG;
        this.cog.setAttribute('aria-label', t.settings);
        this.cog.title = t.settings;
        this.cog.setAttribute('aria-expanded', 'false');
        this.cog.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(); });
        opts.toolbar.insertBefore(this.cog, opts.before);

        this.panel = document.createElement('div');
        this.panel.className = 'ollo-demo-menu';
        this.panel.setAttribute('role', 'menu');
        this.panel.setAttribute('aria-label', t.settings);
        // Taps inside stay inside: the document listener closes on anything else.
        this.panel.addEventListener('pointerdown', (e) => e.stopPropagation());

        if (opts.choices) {
            for (const c of opts.choices) {
                const b = document.createElement('button');
                b.className = 'ollo-demo-menu-item';
                b.type = 'button';
                b.setAttribute('role', 'menuitemradio');
                b.textContent = c.label[resolveLang()];
                b.addEventListener('click', () => {
                    this.mark(c.id);
                    this.close();
                    opts.onChoose(c.id);
                });
                this.buttons.set(c.id, b);
                this.panel.appendChild(b);
            }
            this.mark(opts.initial);
        }

        // Flythrough row: empty until a tour exists (adoptTour).
        this.tourRow = this.row(t.flythrough);
        this.tourRow.hidden = true;
        this.tourRow.addEventListener('click', () => {
            const play = this.tourRow.querySelector<HTMLButtonElement>('.ollo-tour-play');
            // Start, then close so the tour plays unobstructed; a tap on the
            // row while it plays pauses it and leaves the menu up.
            const wasPlaying = play?.classList.contains('playing');
            play?.click();
            if (!wasPlaying) this.close();
        });
        this.panel.appendChild(this.tourRow);

        // Quality row: the Q square unfolds the segments on the row.
        const qRow = this.row(t.quality);
        const q = document.createElement('span');
        q.className = 'ollo-demo-menu-sq';
        q.textContent = 'Q';
        qRow.prepend(q);
        qRow.addEventListener('click', (e) => {
            // A tap on a segment picks it and leaves the row open.
            if ((e.target as HTMLElement).closest('.ollo-quality')) return;
            qRow.classList.toggle('show');
        });
        this.qualitySlot = qRow;
        this.panel.appendChild(qRow);

        opts.parent.appendChild(this.panel);

        this.onDocDown = () => { if (this.opened) this.close(); };
        document.addEventListener('pointerdown', this.onDocDown);
    }

    /** The tour's ▶ moves from the bottom of the screen into the menu. */
    adoptTour(play: HTMLButtonElement): void {
        const sq = document.createElement('span');
        sq.className = 'ollo-demo-menu-sq';
        sq.appendChild(play);
        this.tourRow.prepend(sq);
        this.tourRow.hidden = false;
    }

    /** The scheme button, for the tutorial. */
    button(id: SchemeId): HTMLButtonElement | null {
        return this.buttons.get(id) ?? null;
    }

    get isOpen(): boolean {
        return this.opened;
    }

    open(): void {
        if (this.opened) return;
        this.opened = true;
        this.panel.classList.add('open');
        this.cog.classList.add('active');
        this.cog.setAttribute('aria-expanded', 'true');
        this.opts.onOpen?.();
    }

    close(): void {
        if (!this.opened) return;
        this.opened = false;
        this.panel.classList.remove('open');
        this.cog.classList.remove('active');
        this.cog.setAttribute('aria-expanded', 'false');
        this.opts.onClose?.();
    }

    toggle(): void {
        if (this.opened) this.close();
        else this.open();
    }

    private row(label: string): HTMLElement {
        const r = document.createElement('div');
        r.className = 'ollo-demo-menu-row';
        r.setAttribute('role', 'menuitem');
        const l = document.createElement('span');
        l.className = 'ollo-demo-menu-label';
        l.textContent = label;
        r.appendChild(l);
        return r;
    }

    private mark(id: SchemeId): void {
        for (const [k, b] of this.buttons) {
            b.classList.toggle('active', k === id);
            b.setAttribute('aria-checked', k === id ? 'true' : 'false');
        }
    }

    dispose(): void {
        document.removeEventListener('pointerdown', this.onDocDown);
        this.cog.remove();
        this.panel.remove();
    }
}
